import React, { useMemo, useState } from 'react';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { Badge } from '/imports/ui/Badge';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { getPerBotStats, getDeadStock, getInventoryHealth } from '/imports/api/analytics';

const RANGES = [
  { id: '90d', label: '90d', days: 90 },
  { id: '12m', label: '12m', days: 365 },
  { id: 'all', label: 'All', days: null as number | null },
];

const IDLE_THRESHOLDS = [
  { id: 30, label: '30d' },
  { id: 60, label: '60d' },
  { id: 90, label: '90d' },
  { id: 180, label: '180d' },
];

const int = (n: number) => n.toLocaleString();
const tix = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString()}`;
const signed = (n: number) => `${n > 0 ? '+' : ''}${tix(n)}`;

export const FleetPnlPage = () => {
  const user = useUser();
  const [rangeId, setRangeId] = useState('all');
  const [idleDays, setIdleDays] = useState(60);
  const range = RANGES.find((r) => r.id === rangeId) || RANGES[RANGES.length - 1];

  const arg = useMemo(() => {
    if (range.days == null) return {};
    const to = new Date();
    return { from: new Date(to.getTime() - range.days * 86400000), to };
  }, [range.days]);

  const [perBot, perBotLoading] = useMethod(getPerBotStats, arg, [rangeId]);
  const [dead, deadLoading] = useMethod(getDeadStock, { days: idleDays }, [idleDays]);
  const [health] = useMethod(getInventoryHealth, undefined as any, []);

  if (!user?.isSuperUser) return <NotPermittedPage />;
  const loading = perBotLoading || deadLoading;

  const fleet = perBot?.bots ?? [];
  const totalSpend = fleet.reduce((n, b) => n + b.spend, 0);
  const totalProceeds = fleet.reduce((n, b) => n + b.proceeds, 0);
  const deadShare = dead && dead.heldValue > 0 ? Math.round((dead.totalValue / dead.heldValue) * 100) : null;

  return (
    <Page>
      <PageHeading
        left={<h1>Fleet P&amp;L</h1>}
        right={<ChipGroup ariaLabel="Date range" chips={RANGES.map((r) => ({ id: r.id, label: r.label }))} value={rangeId} onChange={(id) => setRangeId(id)} />}
      />
      <PageBody>
        {loading ? <LoadingFlex /> : (
          <div className="flex flex-col gap-3 pb-6">
            <StatRow>
              <Stat label="Bots trading" value={int(fleet.length)} icon="construction" hint="in the selected range" />
              <Stat label="Tickets out" value={tix(totalSpend)} icon="attachMoney" hint="spent acquiring stock" />
              <Stat label="Tickets in" value={tix(totalProceeds)} icon="attachMoney" hint="received from sales" />
              <Stat
                label="Idle stock"
                value={dead ? tix(dead.totalValue) : '--'}
                icon="documentSearch"
                hint={deadShare != null ? `${deadShare}% of inventory value` : 'nothing held'}
              />
            </StatRow>

            {/*
              This caveat is not decoration. Per-bot cost basis is only sound
              while a bot sells what it bought, and cards move between bots via a
              DEPOT -- replenished from one, depleted back to one -- as friendly
              trades that fileInvoice does not record. The receiving bot then
              books a sale it has no lot for, inflating its margin by the missing
              cost, which is why netFlow is shown alongside.
            */}
            {!!perBot?.totalUnmatched && (
              <Panel className="border-warn/40">
                <div className="text-[13px] text-fg-2">
                  <Badge tone="amber">Depot transfers not in ledger</Badge>
                  <span className="ml-2">
                    {int(perBot.totalUnmatched)} units were sold by a bot with no acquisition of its own for them,
                    which normally means the cards were replenished from a depot. Depot transfers are friendly trades and
                    are not written to the ledger, so per-bot <strong>margin</strong> is overstated for those bots by the
                    missing cost. <strong>Net flow</strong> needs no cost basis and is unaffected, so prefer it.
                  </span>
                </div>
              </Panel>
            )}

            <Panel
              eyebrow="Per bot"
              title="Flows and margin"
              actions={<span className="text-[12px] text-fg-3">{fleet.length ? `${int(fleet.length)} bots` : ''}</span>}
            >
              {fleet.length === 0 ? (
                <div className="flex flex-col items-center gap-1 py-14 text-center">
                  <div className="text-[13px] font-semibold text-fg-1">No trades in this range</div>
                  <div className="text-[12px] text-fg-3">Per-bot figures appear once the fleet records customer trades.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[720px]">
                    <div className="eyebrow grid grid-cols-[1.4fr_repeat(6,minmax(0,1fr))] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Bot</span>
                      <span className="text-right">Trades</span>
                      <span className="text-right">Units in</span>
                      <span className="text-right">Tix out</span>
                      <span className="text-right">Units out</span>
                      <span className="text-right">Tix in</span>
                      <span className="text-right">Net flow</span>
                    </div>
                    {fleet.map((b) => (
                      <div key={b.bot} className="grid grid-cols-[1.4fr_repeat(6,minmax(0,1fr))] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="truncate text-fg-1">{b.bot}</span>
                          {b.unmatchedUnits > 0 && (
                            <span title={`${int(b.unmatchedUnits)} units sold with no lot of this bot's own, most likely replenished from a depot`}>
                              <Badge tone="amber">~</Badge>
                            </span>
                          )}
                        </span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{int(b.trades)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(b.unitsAcquired)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{tix(b.spend)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(b.unitsSold)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{tix(b.proceeds)}</span>
                        <span className={`text-right font-mono font-semibold tabular-nums ${b.netFlow > 0 ? 'text-buy' : b.netFlow < 0 ? 'text-sell' : 'text-fg-3'}`}>
                          {signed(b.netFlow)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Panel>

            <div className="grid gap-3 lg:grid-cols-2">
              {/*
                buycap caps TOTAL ownership per bot -- buybot subtracts what the
                bot already holds -- so a saturated target is silently refused
                rather than logged as a miss. Zero headroom means the fleet
                declines that card outright.
              */}
              <Panel
                eyebrow="Buying capacity"
                title="Targets with no room left"
                actions={
                  <span className="text-[12px] text-fg-3">
                    {health ? `${int(health.saturatedTargets)} of ${int(health.targets)} saturated` : ''}
                  </span>
                }
              >
                {!health || !health.buyingBots ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">No buying bots</div>
                    <div className="text-[12px] text-fg-3">Saturation cannot be judged without active buy, hybrid or cash bots.</div>
                  </div>
                ) : health.saturated.length === 0 ? (
                  <div className="py-10 text-center text-[13px] text-fg-3">No buy targets configured</div>
                ) : (
                  <div className="flex flex-col">
                    <div className="eyebrow grid grid-cols-[1.6fr_auto_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Card</span><span className="text-right">Cap</span><span className="text-right">Held</span><span className="text-right">Room</span>
                    </div>
                    {health.saturated.map((t) => (
                      <div key={t.cardId} className="grid grid-cols-[1.6fr_auto_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="truncate text-fg-1" title={`${t.botsAtCap} of ${health.buyingBots} buying bots at cap`}>{t.card}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{int(t.buycap)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(t.totalHeld)}</span>
                        <span className={`text-right font-mono font-semibold tabular-nums ${t.headroom === 0 ? 'text-sell' : 'text-fg-1'}`}>
                          {t.headroom === 0 ? 'full' : int(t.headroom)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {/*
                Bought above what we now ask. Distinct from dead stock: this card
                may be turning over briskly and still lose money on every copy.
              */}
              <Panel
                eyebrow="Underwater"
                title="Cost above our own ask"
                actions={
                  <span className="text-[12px] text-fg-3">
                    {health?.underwaterLoss ? `${tix(health.underwaterLoss)} tix` : ''}
                  </span>
                }
              >
                {!health || health.pricedLines === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">No cost data</div>
                    <div className="text-[12px] text-fg-3">Nothing in the ledger records what we paid, so nothing can be compared with the ask.</div>
                  </div>
                ) : health.underwater.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">Nothing underwater</div>
                    <div className="text-[12px] text-fg-3">Every priced card is held at or below what we ask for it.</div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="mb-2 text-[12px] text-fg-3">
                      {int(health.underwaterUnits)} units carrying {tix(health.underwaterLoss)} tix of paper loss.
                    </div>
                    <div className="eyebrow grid grid-cols-[1.6fr_auto_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Card</span><span className="text-right">Paid</span><span className="text-right">Ask</span><span className="text-right">Loss</span>
                    </div>
                    {health.underwater.map((u) => (
                      <div key={u.cardId} className="grid grid-cols-[1.6fr_auto_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="truncate text-fg-1" title={`${u.units} held`}>{u.card}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{tix(u.avgCost)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{tix(u.sellPrice)}</span>
                        <span className="text-right font-mono font-semibold tabular-nums text-sell">{tix(u.loss)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            <Panel
              eyebrow="Dead stock"
              title="Held but not moving"
              actions={
                <ChipGroup
                  ariaLabel="Idle threshold"
                  chips={IDLE_THRESHOLDS}
                  value={idleDays}
                  onChange={(id) => setIdleDays(id as number)}
                />
              }
            >
              {!dead || dead.rows.length === 0 ? (
                <div className="flex flex-col items-center gap-1 py-14 text-center">
                  <div className="text-[13px] font-semibold text-fg-1">Nothing idle past {idleDays} days</div>
                  <div className="text-[12px] text-fg-3">
                    {dead?.heldUnits ? `${int(dead.heldUnits)} units held, all with recent movement.` : 'No inventory is currently held.'}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div className="mb-2 text-[12px] text-fg-3">
                    {int(dead.totalUnits)} units worth {tix(dead.totalValue)} tix at our ask, out of {int(dead.heldUnits)} units held.
                  </div>
                  <div className="eyebrow grid grid-cols-[1.6fr_auto_auto_auto_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                    <span>Card</span>
                    <span className="text-right">Units</span>
                    <span className="text-right">Bots</span>
                    <span className="text-right">Idle</span>
                    <span className="text-right">Sold</span>
                    <span className="text-right">Value</span>
                  </div>
                  {dead.rows.map((r) => (
                    <div key={r.cardId} className="grid grid-cols-[1.6fr_auto_auto_auto_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                      <span className="truncate text-fg-1">{r.card}</span>
                      <span className="text-right font-mono tabular-nums text-fg-2">{int(r.units)}</span>
                      <span className="text-right font-mono tabular-nums text-fg-3">{int(r.bots)}</span>
                      {/*
                        "No record" is NOT the same as "never traded", so it is
                        not styled as the strongest signal. Cards also move
                        between bots via a depot, and those friendly trades
                        update `assets` without writing a ledger line -- so an
                        actively circulating card can show no history at all.
                        `neverSold` is the trustworthy signal, because a sale to
                        a customer is always recorded.
                      */}
                      <span className="text-right font-mono tabular-nums text-fg-3">
                        {r.daysIdle == null ? 'no record' : `${int(r.daysIdle)}d`}
                      </span>
                      <span className="text-right">
                        {r.neverSold ? <Badge tone="amber">unsold</Badge> : <span className="text-[12px] text-fg-3">sold</span>}
                      </span>
                      <span className="text-right font-mono font-semibold tabular-nums text-fg-1">{tix(r.valueAtSell)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        )}
      </PageBody>
    </Page>
  );
};
